// THIS IS THE CATALOGUE VIEWER IN TABLE MODE

import { useEffect, useState, useRef, useMemo } from 'react';
import { deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../../firebase';
import {
  Box, TextField, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, TableSortLabel,   IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, Button, Select, MenuItem, Switch, Tooltip,Snackbar, Alert, AlertTitle
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import AddIcon from '@mui/icons-material/Add';
import { saveDevice, tidyNames } from '../services/catalogueService';

import { listPricingLevels } from '../services/pricingService';
import { TYPES, IMAGE_SETS } from '../services/staticLists';

export default function CatalogueViewer({ rows, setRows }) {
  const [filter, setFilter] = useState('');
  const [orderBy, setOrderBy] = useState('cat_1');
  const [order, setOrder] = useState('asc');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editedRow, setEditedRow] = useState({});
  const [priceOptions, setPriceOptions] = useState([]);
  const wrapperRef = useRef(null);
  const [selectedId, setSelectedId] = useState(null);
  const [validation, setValidation] = useState({});     // {rowId:{cat_?:'error'|'warn'}}
  const REQUIRED = ['type', 'cat_1', 'cat_2', 'price_level', 'image_set'];

  const columnDefs = [
    { key: 'type', sx: { minWidth: 50, whiteSpace: 'nowrap', p: 1 }, align: 'left' },
    { key: 'cat_1', sx: { minWidth: 50, whiteSpace: 'nowrap', p: 1 }, align: 'left' },
    { key: 'cat_2', sx: { minWidth: 50, whiteSpace: 'nowrap', p: 1 }, align: 'left' },
    { key: 'cat_3', sx: { minWidth: 50, whiteSpace: 'nowrap', p: 1 }, align: 'left' },
    { key: 'cat_4', sx: { minWidth: 50, whiteSpace: 'nowrap', p: 1 }, align: 'left' },
    { key: 'cat_5', sx: { minWidth: 50, whiteSpace: 'nowrap', p: 1 }, align: 'left' },
    { key: 'cat_6', sx: { minWidth: 50, whiteSpace: 'nowrap', p: 1 }, align: 'left' },
    { key: 'cat_7', sx: { minWidth: 50, whiteSpace: 'nowrap', p: 1 }, align: 'left' },
    { key: 'cat_8', sx: { minWidth: 50, whiteSpace: 'nowrap', p: 1 }, align: 'left' },
    { key: 'cat_9', sx: { minWidth: 50, whiteSpace: 'nowrap', p: 1 }, align: 'left' },
    { key: 'cat_10', sx: { minWidth: 50, whiteSpace: 'nowrap', p: 1 }, align: 'left' },
    { key: 'price_level', sx: { minWidth: 150 }, align: 'center' },
    { key: 'width', sx: { width: 80 }, align: 'center' },
    { key: 'height', sx: { width: 80 }, align: 'center' },
    { key: 'thickness', sx: { width: 90 }, align: 'center' },
    { key: 'extra_padding', sx: { width: 120 }, align: 'center' },
    { key: 'image_set', sx: { minWidth: 180 }, align: 'center' },
    { key: 'Live', sx: { width: 70 }, align: 'center' },
    { key: 'UID', sx: { width: 240, }, align: 'left' }
  ];


  useEffect(() => {
    listPricingLevels().then(setPriceOptions);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        editingId &&
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target) &&
        !document.querySelector('.MuiPopover-root')?.contains(e.target)
      ) {
        clearValidation(editingId);
        setEditingId(null);
        setEditedRow(prev => {
          setSelectedId(prev?.id || null);
          return {};
        });

      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [editingId]);

  /* ───────────────────────────────────────────────────────────── */
  /* snackbar + auto-scroll                                       */
  const [snack, setSnack]           = useState({ open: false, msg: '' });
  const [scrollTarget, setTarget]   = useState(null);
  const showSnack   = msg => setSnack({ open: true, msg });
  const closeSnack  = ()  => setSnack({ open: false, msg: '' });

  useEffect(() => {
    if (!scrollTarget) return;
    /* allow DOM to paint first */
    const t = setTimeout(() => {
      const el = document.getElementById(scrollTarget);
      if (el) el.scrollIntoView({ behaviour: 'smooth', block: 'center' });
      setSelectedId(scrollTarget);      // highlight
      setTarget(null);
    }, 0);
    return () => clearTimeout(t);
  }, [rows, scrollTarget]);


  // ─ helper: required-fields + cat-gap/duplicate check ───────────
  const hasError = useMemo(() => {
    if (!editingId) return false;
    const gapError = Object.values(validation[editingId] || {}).some(v => v === 'error' || v === 'branch');
    const missingReq = REQUIRED.some(k => !(editedRow[k] ?? '').toString().trim());
    return gapError || missingReq;
  }, [editingId, validation, editedRow]);


  const handleSort = (col) => {
    if (orderBy === col) {
      setOrder(order === 'asc' ? 'desc' : 'asc');
    } else {
      setOrderBy(col);
      setOrder('asc');
    }
  };

  const handleDelete = async () => {
    if (!rowToDelete) return;
    await deleteDoc(doc(db, 'catalogue', rowToDelete.id));
    setRows(prev => prev.filter(row => row.id !== rowToDelete.id));
    setConfirmOpen(false);
    setRowToDelete(null);
  };

  /* ------------------------------------------------------------------ */
  /*  Save logic                                                         */
  /* ------------------------------------------------------------------ */

  const handleSave = async () => {
    if (hasError) return;

    try {
      if (editedRow.id.startsWith('temp-')) {
        const { id, ...payload } = editedRow;
        const uid = await saveDevice(payload);              // runs duplicate check
        setRows(r => r.map(x =>
          x.id === id ? { ...tidyNames(editedRow), id: uid, UID: uid } : x));
       setTarget(uid);                                // ← jump here
       showSnack('New sleeve saved');
        clearValidation(id);                 // drop temp key
      } else {
        // await updateDoc(doc(db, 'catalogue', editedRow.id), editedRow);
        // setRows(r => r.map(x => x.id === editedRow.id ? editedRow : x));
        // Use the central saveDevice function for edits to ensure consistency.
        await saveDevice(editedRow);
        // Update the local state with the data that was just saved.
        setRows(r => r.map(x => x.id === editedRow.id ? tidyNames(editedRow) : x));

        showSnack('Changes saved');
        clearValidation(editedRow.id);         // drop any warn/error tags
      }

      setEditingId(null);
      setEditedRow({});
      setSelectedId(editedRow.id);
    } catch (err) {
      alert(err.message);                                   // duplicate etc.
    }
  };

  const filtered = rows.filter(row =>
    Object.values(row).some(v =>
      typeof v === 'string' && v.toLowerCase().includes(filter.toLowerCase())
    )
  );

  const sorted = [...filtered].sort((a, b) => {
    const valA = a[orderBy] ?? '';
    const valB = b[orderBy] ?? '';
    return order === 'asc'
      ? valA.toString().localeCompare(valB.toString())
      : valB.toString().localeCompare(valA.toString());
  });


  const handleKeyDown = (e) => {
    const index = sorted.findIndex(r => r.id === selectedId);

    if (e.key === 'Escape') {
      clearValidation(editingId);
      setEditingId(null);
      setEditedRow(prev => {
        setSelectedId(prev?.id || null);
        return {};
      });
    }

    if (e.key === 'ArrowDown' && index < sorted.length - 1) {
      setSelectedId(sorted[index + 1].id);
      e.preventDefault();
    }

    if (e.key === 'ArrowUp' && index > 0) {
      setSelectedId(sorted[index - 1].id);
      e.preventDefault();
    }

    if (e.key === 'Enter' && selectedId && !editingId) {
      const row = sorted.find(r => r.id === selectedId);
      if (row) {
        setEditingId(row.id);
        setEditedRow(row);
      }
    }
  };

  /* ------------------------------------------------------------------ */
  /*  Helpers                                                            */
  /* ------------------------------------------------------------------ */
  const catKeys = useMemo(() => Array.from({ length: 10 }, (_, i) => `cat_${i + 1}`), []);

  /* which cat_xxx fields should be editable?  */
  const editableCats = useMemo(() => {
    if (!editingId) return [];                 // not in edit-mode
    const lastFilled = catKeys.findLastIndex(k => (editedRow[k] ?? '') !== '');
    const maxIdx = Math.min(lastFilled + 1, catKeys.length - 1); // +1 blank
    return catKeys.filter((_, idx) => idx <= maxIdx);
  }, [editingId, editedRow, catKeys]);

  const clearValidation = (rowId) => {
    if (!rowId) return;
    setValidation(prev => {
      const { [rowId]: _omit, ...rest } = prev;   // drop that key
      return rest;
    });
  };




  // ─ add-row (top-right button) ──────────────────────────────────
  const handleAddRow = () => {
    const tempId = `temp-${Date.now()}`;
    const blank = { id: tempId, UID: '', Live: false };
    catKeys.forEach(k => (blank[k] = ''));
    setRows(r => [blank, ...r]);
    setEditedRow(blank);
    setEditingId(tempId);
  };

  // ─ duplicate current row ───────────────────────────────────────
  const handleDuplicate = (row) => {
    const tempId = `temp-${Date.now()}`;
    const clone = { ...row, id: tempId, UID: '' };
    setRows(r => {
      const idx = r.findIndex(x => x.id === row.id);
      const out = [...r];
      out.splice(idx + 1, 0, clone);
      return out;
    });
    setEditedRow(clone);
    setEditingId(tempId);
  };

  /* validate editedRow – returns {cat_3:'error'|'warn', … } */
  const validateCats = (draft, originalRow) => {
    const out = {};
    const cats = catKeys.map(k => draft[k] ?? '');

    // rule-1 / rule-3 – gaps not allowed
    cats.forEach((v, idx) => {
      if (!v && cats.slice(idx + 1).some(Boolean)) out[catKeys[idx]] = 'error';
    });

    // rule-4 – change to higher-level cat that still has siblings
    const lastFilled = cats.findLastIndex(Boolean);
    cats.forEach((v, idx) => {
      if (!v) return;
      if (idx < lastFilled && v !== originalRow[catKeys[idx]]) {
        const parentMatch = rows.filter(r =>
          catKeys.slice(0, idx + 1).every((k, i) =>
            (i === idx ? r[k] === originalRow[k] : r[k] === cats[i]))
          && r.id !== originalRow.id
        );
        if (parentMatch.length) out[catKeys[idx]] = 'warn';
      }
    });

  /* ── rule-5  ·  leaf-only device check ─────────────────────────
     A catalogue row must NOT be a proper prefix of another row
     (i.e. no node can be both a device and a branch).           */
  const pathA = cats.slice(0, lastFilled + 1);          // current path
  const clash = rows.some(r => {
    if (r.id === originalRow.id) return false;          // ignore self
    const pathB = catKeys.map(k => r[k] ?? '').filter(Boolean);
    if (!pathB.length) return false;
    const minLen = Math.min(pathA.length, pathB.length);
    const same   = pathA.slice(0, minLen).every((v, i) => v === pathB[i]);
    return same && pathA.length !== pathB.length;       // prefix relation
  });
    if (clash) out[catKeys[lastFilled]] = 'branch';
    return out;
  };


  const renderEditableCell = (col, row) => {
    const value = editedRow[col];
    const handleChange = val => setEditedRow(prev => ({ ...prev, [col]: val }));
const reqMissing = REQUIRED.includes(col) &&
                  !String(value ?? '').trim();

    if (col === 'type') {
      return (
        <Select size="small" value={value || ''} onChange={e => handleChange(e.target.value)} fullWidth MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}>
          {TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
        </Select>
      );
    }
    if (col === 'image_set') {
      return (
        <Select size="small" value={value || ''} onChange={e => handleChange(e.target.value)} fullWidth MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}>
          {IMAGE_SETS.map(set => <MenuItem key={set} value={set}>{set}</MenuItem>)}
        </Select>
      );
    }
    if (col === 'price_level') {
      return (
        <Select size="small" value={value || ''} onChange={e => handleChange(e.target.value)} fullWidth MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}>
          {priceOptions.map(opt => (
            <MenuItem key={opt.level} value={opt.level}>
              {opt.level} - £{opt.price.toFixed(2)}
            </MenuItem>
          ))}
        </Select>
      );
    }
    if (['width', 'height', 'thickness', 'extra_padding'].includes(col)) {
      return (
        <TextField size="small" type="number" value={value || 0} onChange={e => handleChange(Number(e.target.value))} fullWidth inputProps={{ style: { padding: '6px' } }} />
      );
    }
    if (col === 'Live') {
      return (
        <Switch
          checked={!!value}
          onChange={e => handleChange(e.target.checked)}
        />
      );
    }
    if (catKeys.includes(col)) {
      /* show an input only if this cat_xxx is allowed */
      if (!editableCats.includes(col)) {
        return row[col] || '';                   // plain read-only cell
      }

      const err = validation[row.id]?.[col];

      return (
        <TextField
          size="small"
          value={value || ''}
          onChange={e => {
            const next = { ...editedRow, [col]: e.target.value };
            setEditedRow(next);
            setValidation(v => ({ ...v, [row.id]: validateCats(next, row) }));
          }}
        error={err === 'error' || err === 'branch' || reqMissing}
        helperText={
          err === 'error'     ? 'No gaps allowed'
          : err === 'branch'  ? 'Node cannot be both branch and device'
          : reqMissing        ? 'Required'
          : ''
        }
          sx={{
            backgroundColor: err === 'warn' ? 'warning.light' : 'inherit',
            p: 0
          }}
        />
      );
    }

    if (col === 'UID') {
      return row.UID;
    }

    return row[col];
  };

  return (
    <Box sx={{ p: 3, overflowX: 'hidden' }}>
      <Alert severity="warning" sx={{ mb: 3 }}>
        <AlertTitle>Grid View Requires Refactoring</AlertTitle>
        This component is currently benched due to performance and data model drift. Before re-enabling, please complete the following:
        <ul>
          <li><strong>Virtualization:</strong> Replace the standard Table rendering with <code>react-window</code> or pagination to handle large datasets without freezing the browser.</li>
          <li><strong>Schema Drift:</strong> Update hardcoded <code>type</code> references to <code>Type</code> to match the live Firestore data.</li>
          <li><strong>Accessories Arrays:</strong> The Grid attempts to render the native JS array from the database directly into the text fields. It must be updated to ignore accessories arrays and provide a proper UI (like the Drawer's chips).</li>
          <li><strong>Validation Logic:</strong> The <code>validateCats</code> function needs to know about <code>isAccessoriesArray</code> so it doesn't mistakenly flag leaf nodes as branches.</li>
        </ul>
      </Alert>
      <Box display="flex" justifyContent="flex-end" mb={1}>
        <Button variant="contained" size="small"
                startIcon={<AddIcon />} onClick={handleAddRow}>
          Add row
        </Button>
      </Box>
      <TextField
        label="Search"
        variant="outlined"
        size="small"
        sx={{ mb: 2 }}
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
      /><Box
        tabIndex={0}
        onKeyDown={handleKeyDown}
        sx={{ outline: 'none' }}
      >
        <TableContainer component={Paper} sx={{ overflow: 'auto', maxHeight: 'calc(100vh - 160px)', }}>
          <Table stickyHeader size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'grey.200' }}>
                {columnDefs.map(({ key, sx, align }) => (
                  <TableCell key={key} sx={{
                    ...sx,
                    backgroundColor: theme => theme.palette.grey[200]
                  }} align={align || 'left'}>
                    <TableSortLabel
                      active={orderBy === key}
                      direction={orderBy === key ? order : 'asc'}
                      onClick={() => handleSort(key)}
                      hideSortIcon={orderBy !== key}
                    >
                      {key}
                    </TableSortLabel>
                  </TableCell>
                ))}
                <TableCell sx={{ backgroundColor: theme => theme.palette.grey[200],width: 90 }} align='center'>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sorted.map(row => (
                <TableRow
                  key={row.id}
                  id={row.id}
                  ref={editingId === row.id ? wrapperRef : null}
                  onClick={() => setSelectedId(row.id)}
                  onDoubleClick={() => {
                    setEditingId(row.id);
                    setEditedRow(row);
                  }}
                  sx={{
                    backgroundColor:
                      editingId === row.id
                        ? 'background.paper'
                        : selectedId === row.id
                          ? 'action.selected'
                          : 'inherit',
                    boxShadow: editingId === row.id ? 3 : 'none',
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor:
                        editingId === row.id ? 'background.paper' : '#f5f5f5'
                    }
                  }}

                >


                  {columnDefs.map(({ key, sx, align }) => (
                    <TableCell key={key} sx={sx} align={align || 'left'}>
                      {editingId === row.id
                        ? renderEditableCell(key, row)
                        : key === 'Live' ? (
                          <Switch
                            checked={!!row[key]}
                            onChange={(e) => {
                              const newValue = e.target.checked;
                              updateDoc(doc(db, 'catalogue', row.id), { Live: newValue });
                              setRows(prev => prev.map(r => r.id === row.id ? { ...r, Live: newValue } : r));
                            }}
                          />
                        ) : row[key]}
                    </TableCell>
                  ))}
                  <TableCell align='center'>
                    {editingId === row.id ? (
                      <IconButton size="small" onClick={handleSave} disabled={hasError}>
                         <Tooltip title="Save changes"arrow><SaveIcon fontSize="small" /></Tooltip>
                      </IconButton>
                    ) : (
                      <IconButton size="small" onClick={() => {
                        setEditingId(row.id);
                        setEditedRow(row);
                      }}>
                        <Tooltip title="Edit this device" arrow><EditIcon fontSize="small" /></Tooltip>
                      </IconButton>
                    )}
                      <IconButton size="small" onClick={() => handleDuplicate(row)}>
                        <Tooltip title="Duplicate this device" arrow><FileCopyIcon fontSize="small" /></Tooltip>
                      </IconButton>

                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => {
                        setRowToDelete(row);
                        setConfirmOpen(true);
                      }}
                    >
                      <Tooltip title="Delete this device" arrow><DeleteIcon fontSize="small" /></Tooltip>
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table><Snackbar
  open={snack.open}
  autoHideDuration={3000}
  onClose={closeSnack}
  anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
  message={snack.msg}
/>
        </TableContainer>
      </Box>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Delete this row?</DialogTitle>
        <DialogContent>
          Are you sure you want to permanently delete:
          <pre>{JSON.stringify(rowToDelete, null, 2)}</pre>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
